/**
 * 🏛️ FONTE CANÔNICA ÚNICA DE TIPOS DE ESPAÇO — BERASTAYS
 *
 * Esta lista é a única referência válida para tipos de imóvel em toda a
 * plataforma (cadastro, edição, ofertas, filtros, categorias na home,
 * admin). Não inventar/duplicar tipos noutros ficheiros — importar daqui.
 *
 * Cobertura: mercado africano + internacional.
 *
 * Mantém compatibilidade com dados antigos (apartamento, moradia, suite,
 * room, etc.) através de `aliases`. O `value` é exatamente o que é
 * gravado em `properties.property_type`.
 */

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import {
  ApartmentIcon,
  HotelBuildingIcon,
  GuesthouseIcon,
  ResortIcon,
  RoomIcon,
  ContainerIcon,
  YachtIcon,
  VillaIcon,
  CountryHouseIcon,
  VacationHomeIcon,
  BedAndBreakfastIcon,
  HouseIcon,
} from "@/components/icons/PropertyTypeIcons";

export type PropertyTypeDef = {
  /** Valor canônico gravado em DB (case-sensitive). */
  value: string;
  /** Singular para forms/seletores. */
  label: string;
  /** Plural usado nas categorias da home. */
  labelPlural: string;
  /** Componente de ícone (custom SVG ou Lucide). */
  Icon: React.FC<any>;
  /** Tokens de cor (Tailwind) para a UI. */
  gradient: string;
  bgLight: string;
  iconColor: string;
  /** Variantes históricas / minúsculas / sinónimos para matching. */
  aliases: string[];
  /** Curta descrição opcional. */
  hint?: string;
};

export const PROPERTY_TYPES: PropertyTypeDef[] = [
  {
    value: "Apartamento",
    label: "Apartamento",
    labelPlural: "Apartamentos",
    Icon: ApartmentIcon,
    gradient: "from-blue-500 to-blue-600",
    bgLight: "from-blue-100 to-indigo-50",
    iconColor: "text-blue-700",
    aliases: ["apartamento", "apartment", "apt", "flat"],
  },
  {
    value: "Hotel",
    label: "Hotel",
    labelPlural: "Hotéis",
    Icon: HotelBuildingIcon,
    gradient: "from-indigo-500 to-indigo-600",
    bgLight: "from-purple-100 to-violet-50",
    iconColor: "text-purple-700",
    aliases: ["hotel", "hoteis", "hotéis"],
  },
  {
    value: "Hospedaria",
    label: "Hospedaria",
    labelPlural: "Hospedarias",
    Icon: GuesthouseIcon,
    gradient: "from-amber-500 to-amber-600",
    bgLight: "from-amber-100 to-yellow-50",
    iconColor: "text-amber-700",
    aliases: ["hospedaria", "hostel", "pousada", "pensao", "pensão"],
  },
  {
    value: "Resort",
    label: "Resort",
    labelPlural: "Resorts",
    Icon: ResortIcon,
    gradient: "from-orange-500 to-orange-600",
    bgLight: "from-orange-100 to-amber-50",
    iconColor: "text-orange-700",
    aliases: ["resort"],
  },
  {
    value: "Quarto T1",
    label: "Quarto T1",
    labelPlural: "Quartos T1",
    Icon: RoomIcon,
    gradient: "from-pink-500 to-pink-600",
    bgLight: "from-pink-100 to-rose-50",
    iconColor: "text-pink-700",
    aliases: ["quarto t1", "t1", "quarto", "room", "suite", "suíte"],
    hint: "Estúdio com um quarto",
  },
  {
    value: "Contentor",
    label: "Contentor",
    labelPlural: "Contentores",
    Icon: ContainerIcon,
    gradient: "from-slate-500 to-slate-600",
    bgLight: "from-slate-100 to-gray-50",
    iconColor: "text-slate-700",
    aliases: ["contentor", "container", "contentor adaptado"],
  },
  {
    value: "Barco",
    label: "Barco",
    labelPlural: "Barcos",
    Icon: YachtIcon,
    gradient: "from-sky-500 to-sky-600",
    bgLight: "from-sky-100 to-blue-50",
    iconColor: "text-sky-700",
    aliases: ["barco", "boat", "yacht", "iate"],
  },
  {
    value: "Vivenda",
    label: "Vivenda",
    labelPlural: "Vivendas",
    Icon: VillaIcon,
    gradient: "from-violet-500 to-violet-600",
    bgLight: "from-violet-100 to-purple-50",
    iconColor: "text-violet-700",
    aliases: ["vivenda", "villa", "moradia"],
  },
  {
    value: "Casa de Campo",
    label: "Casa de Campo",
    labelPlural: "Casas de Campo",
    Icon: CountryHouseIcon,
    gradient: "from-emerald-500 to-emerald-600",
    bgLight: "from-emerald-100 to-green-50",
    iconColor: "text-emerald-700",
    aliases: ["casa de campo", "campo", "rural", "fazenda", "country house"],
    hint: "Refúgio rural",
  },
  {
    value: "Casa de Praia",
    label: "Casa de Praia",
    labelPlural: "Casas de Praia",
    Icon: VacationHomeIcon,
    gradient: "from-cyan-500 to-cyan-600",
    bgLight: "from-cyan-100 to-sky-50",
    iconColor: "text-cyan-700",
    aliases: ["casa de praia", "beach house", "praia", "beachhouse"],
  },
  {
    value: "Guest House",
    label: "Guest House",
    labelPlural: "Guest Houses",
    Icon: BedAndBreakfastIcon,
    gradient: "from-rose-500 to-rose-600",
    bgLight: "from-rose-100 to-pink-50",
    iconColor: "text-rose-700",
    aliases: ["guest house", "guesthouse", "casa de hóspedes", "casa de hospedes"],
  },
  {
    value: "Suite Privada",
    label: "Suite Privada",
    labelPlural: "Suites Privadas",
    Icon: HouseIcon,
    gradient: "from-fuchsia-500 to-fuchsia-600",
    bgLight: "from-fuchsia-100 to-pink-50",
    iconColor: "text-fuchsia-700",
    aliases: ["suite privada", "private suite", "suite", "suíte privada"],
  },
];

/** Lista plana de valores canônicos (para selects simples). */
export const PROPERTY_TYPE_VALUES: string[] = PROPERTY_TYPES.map((t) => t.value);

/** Mapa rápido alias → definição canônica (chaves em lowercase). */
const ALIAS_MAP: Record<string, PropertyTypeDef> = (() => {
  const m: Record<string, PropertyTypeDef> = {};
  for (const t of PROPERTY_TYPES) {
    m[t.value.toLowerCase()] = t;
    for (const a of t.aliases) m[a.toLowerCase()] = t;
  }
  return m;
})();

/** Resolve qualquer string (legacy/canônica/plural) para a definição canônica. */
export function resolvePropertyType(input: string | null | undefined): PropertyTypeDef | null {
  if (!input) return null;
  return ALIAS_MAP[String(input).trim().toLowerCase()] || null;
}

/** Label canônico para exibição. Devolve o input se desconhecido. */
export function getPropertyTypeLabel(input: string | null | undefined): string {
  return resolvePropertyType(input)?.label || (input || "");
}
